/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:59]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DropDown,
    minRule,
    requiredRule,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import { LeadgenInfo } from 'models/api/leadgen';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import i18n from 'utils/i18n';
import '../Leadgen.scss';

const emptyItem = {
    id: null,
    orderStatus: '',
    postbackUrl: '',
    clientType: '',
    scheduleId: '',
    isActive: false,
    applyLimit: false,
    limitMin: null,
    limitMax: null,
    isDeleted: false,
};

type LeadgenModalPostbackParametersProps = { disabled: boolean };

const LeadgenModalPostbackParameters = ({ disabled }: LeadgenModalPostbackParametersProps) => {
    const api = useApi();
    const { t } = useTranslation();
    const { setValue, getValues, watch } = useFormContext<LeadgenInfo>();
    const [orderStatuses, setOrderStatuses] = useState<DropDown[]>([]);
    const [clientTypes, setClientTypes] = useState<DropDown[]>([]);
    const [sheduleTypes, setSheduleTypes] = useState<DropDown[]>([]);

    const dictsQuery = useQuery(
        'leadgenModalPostbackParametersDicts',
        () =>
            getSelectList(
                api,
                ['OrderStatus', 'ClientTypeForLeadgen', 'ScheduleTypeForLeadgen'],
                i18n.language
            ),
        {
            onSuccess: (data) => {
                setOrderStatuses([{ id: null, name: '' }, ...data['OrderStatus']]);
                setClientTypes([{ id: null, name: '' }, ...data['ClientTypeForLeadgen']]);
                setSheduleTypes([{ id: null, name: '' }, ...data['ScheduleTypeForLeadgen']]);
            },
            enabled: !orderStatuses.length && !clientTypes.length && !sheduleTypes.length,
        }
    );

    const addPostbackParameter = () => {
        setValue('postbackParameters', [...getValues().postbackParameters, emptyItem]);
    };

    const deletePostbackParameter = (id: number | null, ids: number) => {
        setValue(
            'postbackParameters',
            getValues().postbackParameters.map((x, i) =>
                i === ids ? { ...x, isDeleted: true } : x
            )
        );
    };

    const postbackParameters = watch('postbackParameters');

    return (
        <>
            {postbackParameters && postbackParameters.length ? (
                postbackParameters.map(
                    (item, ids) =>
                        !item.isDeleted && (
                            <Card className="mb-3" key={ids}>
                                <CardHeader className="leadgen-modal__card-header">
                                    <>
                                        {item.id ? `#${item.id}` : t('BackOfficeNew')}
                                        <Button
                                            color="danger"
                                            onClick={() => deletePostbackParameter(item.id, ids)}
                                            disabled={disabled}
                                        >
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </Button>
                                    </>
                                </CardHeader>
                                <CardBody>
                                    <ValidatedInput<LeadgenInfo>
                                        type="checkbox"
                                        name={`postbackParameters.${ids}.isActive`}
                                        className="mb-3"
                                        label={t('BackOfficeIsActive')}
                                        disabled={disabled}
                                    />
                                    <ValidatedDropDownList<LeadgenInfo>
                                        name={`postbackParameters.${ids}.orderStatus`}
                                        label={t('BackOfficeOrderStatus')}
                                        items={orderStatuses}
                                        isLoading={dictsQuery.isFetching}
                                        disabled={disabled}
                                        rules={{ required: requiredRule }}
                                    />
                                    <ValidatedInput<LeadgenInfo>
                                        type="textarea"
                                        height={80}
                                        name={`postbackParameters.${ids}.postbackUrl`}
                                        label={t('BackOfficePostbackUrl')}
                                        disabled={disabled}
                                        rules={{ required: requiredRule }}
                                    />
                                    <ValidatedDropDownList<LeadgenInfo>
                                        name={`postbackParameters.${ids}.clientType`}
                                        label={t('BackOfficeClientType')}
                                        items={clientTypes}
                                        isLoading={dictsQuery.isFetching}
                                        disabled={disabled}
                                        rules={{ required: requiredRule }}
                                    />
                                    <ValidatedDropDownList<LeadgenInfo>
                                        name={`postbackParameters.${ids}.scheduleId`}
                                        label={t('BackOfficeSchedule')}
                                        items={sheduleTypes}
                                        isLoading={dictsQuery.isFetching}
                                        disabled={disabled}
                                        rules={{ required: requiredRule }}
                                    />
                                    <ValidatedInput<LeadgenInfo>
                                        id={`postback-parameter-${ids}-apply-limit-id`}
                                        type="checkbox"
                                        name={`postbackParameters.${ids}.applyLimit`}
                                        className="mb-3"
                                        label={t('BackOfficeApplyLimit')}
                                        disabled={disabled}
                                    />
                                    {item.applyLimit && (
                                        <Row>
                                            <Col>
                                                <ValidatedInput<LeadgenInfo>
                                                    type="number"
                                                    name={`postbackParameters.${ids}.limitMin`}
                                                    className="mb-3"
                                                    label={t('BackOfficeMinAmount')}
                                                    disabled={disabled}
                                                    rules={{ required: requiredRule }}
                                                />
                                            </Col>
                                            <Col>
                                                <ValidatedInput<LeadgenInfo>
                                                    type="number"
                                                    name={`postbackParameters.${ids}.limitMax`}
                                                    className="mb-3"
                                                    label={t('BackOfficeMaxAmount')}
                                                    disabled={disabled}
                                                    rules={{
                                                        required: requiredRule,
                                                        min: minRule(item.limitMin),
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        )
                )
            ) : (
                <></>
            )}
            <Button color="secondary" onClick={addPostbackParameter} disabled={disabled}>
                <FontAwesomeIcon icon={faPlus} /> <Trans i18nKey="BackOfficeAdd" />
            </Button>
        </>
    );
};

export default LeadgenModalPostbackParameters;
